import Button from "@/Layouts/components/Buttons/Button";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import LabeledFormInput from "@/Layouts/components/SmartAddressFormSection/LabeledFormInput";
import { AddCircleRounded } from "@mui/icons-material";
import { Box, Grid, Typography, SvgIcon } from "@mui/material";
import { CSSProperties, useState } from "react";

// Libraries
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from "lodash";
import clsx from "clsx";

// Constants
import { EMPTY_PHONE_NUMBER, REQUIRED_FIELD_CLASSES, TYPE_OF_PHONES } from "../../../constants";

// Context
import { useLeadsContext } from "@/DataPages/context/crm/LeadsContext";

// Components
import { PhoneNumberInput } from "@/Layouts/components/InputComponents/PhoneNumberInput";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// Interfaces
import { IPhone } from "@/DataPages/context/crm/interfaces";

// Utils
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import { isAnyPhoneTypeAvailable } from "../../../helpers";
import { areValidAllPhoneNumbers, isValidPhoneNumber } from "@/DataPages/context/crm/LeadsContext/helpers";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/common/trash-icon.svg";

interface ICustomerContactSectionProps {
	sx?: CSSProperties;
	className?: string;
}

function CustomerContactSection(props: Readonly<ICustomerContactSectionProps>) {
	const { sx, className } = props ?? {};

	/* Context */
	const { isSavingLead, getContextError, onChangeContext } = useLeadsContext();

	/* States */
	const [email, setEmail] = useState<string>("");
	const [phoneNumbers, setPhoneNumbers] = useState<Array<IPhone>>([{ ...EMPTY_PHONE_NUMBER, id: uuidv4() }]);

	/* Handlers */
	function handleChangeEmail(value = "") {
		setEmail(value);
		onChangeContext("customer", "email", value);
	}

	function handleChangePhone(index: number, field: keyof IPhone, value: string) {
		const didItChange = phoneNumbers[index][field] !== value;

		if (!didItChange) {
			return;
		}

		const updatedPhones = cloneDeep(phoneNumbers);
		updatedPhones[index][field] = value;
		setPhoneNumbers(updatedPhones);
		onChangeContext("customer", "phones", updatedPhones);
	}

	function handleAddPhoneNumber() {
		if (!areValidAllPhoneNumbers(phoneNumbers)) {
			return;
		}

		const newPhones = cloneDeep(phoneNumbers);
		newPhones.push(EMPTY_PHONE_NUMBER);
		setPhoneNumbers(newPhones);

		onChangeContext("customer", "phones", newPhones);
	}

	function handleRemovePhoneNumber(index: number) {
		const updatedPhones = phoneNumbers.filter((_, i) => i !== index);
		setPhoneNumbers(updatedPhones);
		onChangeContext("customer", "phones", updatedPhones);
	}

	return (
		<Box sx={sx} className={className}>
			<Typography className="!font-montserrat !font-[500] !text-[16px] !text-gray-text !mb-3">Contact</Typography>
			<EmailComponent
				email={email}
				handleChangeEmail={handleChangeEmail}
				error={getContextError("customer", "email")}
				disabled={isSavingLead}
			/>

			{phoneNumbers.map((phone, index) => {
				return (
					<PhoneNumberComponent
						key={`phone-number-input-${phone.id}`}
						isDefaultPhoneField={index !== 0}
						phoneNumber={phone?.number}
						phoneType={phone?.type}
						handleChangePhone={(field, value) => handleChangePhone(index, field, value)}
						removePhoneNumber={() => handleRemovePhoneNumber(index)}
						selectedTypePhones={phoneNumbers
							.map((phoneValue) => phoneValue.type)
							.filter((type): type is string => typeof type === "string" && !!type)}
						error={getContextError("customer", "phones")}
						disabled={isSavingLead}
					/>
				);
			})}

			<Grid container spacing={2}>
				<Grid item xs={12} className="!mt-1">
					<Box className="flex justify-start">
						<Button
							classes={clsx({
								"!normal-case !font-sans !font-[400] !text-[14px] !min-h-[30px] !rounded-[6px]": true,
								"!bg-primary": true,
								"!bg-disabled !text-white": isSavingLead || !isAnyPhoneTypeAvailable(phoneNumbers),
							})}
							disabled={isSavingLead || !isAnyPhoneTypeAvailable(phoneNumbers)}
							variant="contained"
							text="Add Phone Number"
							onClick={handleAddPhoneNumber}
							endIcon={<AddCircleRounded />}
						/>
					</Box>
				</Grid>
			</Grid>
		</Box>
	);
}

type TEmailComponentProps = {
	email?: string;
	error: boolean;
	disabled: boolean;
	handleChangeEmail: (value?: string) => void;
};
function EmailComponent(props: Readonly<TEmailComponentProps>) {
	const { email = "", error, disabled, handleChangeEmail } = props;

	return (
		<Grid container spacing={2} className="!mb-3">
			<Grid item xs={12} sm={4}>
				<LabeledFormInput
					label="Email"
					formInput={
						<SimpleTextField
							editable
							outlined
							text={email}
							placeholder={"Email"}
							error={error}
							classes={error ? REQUIRED_FIELD_CLASSES : ""}
							onFocusOut={handleChangeEmail}
							disabled={disabled}
							inputProps={{ maxLength: 62 }}
						/>
					}
				/>
			</Grid>
		</Grid>
	);
}

type TPhoneNumberComponentProps = {
	phoneNumber?: string;
	phoneType?: string;
	isDefaultPhoneField: boolean;
	selectedTypePhones: Array<string>;
	error: boolean;
	disabled: boolean;
	handleChangePhone: (field: keyof IPhone, value: string) => void;
	removePhoneNumber: () => void;
};

function PhoneNumberComponent(props: Readonly<TPhoneNumberComponentProps>) {
	const {
		phoneNumber = "",
		phoneType = "",
		isDefaultPhoneField,
		selectedTypePhones,
		error,
		disabled,
		handleChangePhone,
		removePhoneNumber,
	} = props;

	return (
		<Grid container spacing={2} className="!mb-3">
			<Grid item xs={12} sm={4}>
				<LabeledFormInput
					label="Phone Number"
					formInput={
						<PhoneNumberInput
							defaultValue={"us"}
							placeholder="+1 (123) 456-7890"
							value={phoneNumber}
							disabled={disabled}
							error={error && !isValidPhoneNumber(phoneNumber)}
							onChange={(__, ___, event) => {
								handleChangePhone("number", event.target.value);
							}}
						/>
					}
				/>
			</Grid>
			<Grid item xs={12} sm={4}>
				<LabeledFormInput
					label="Phone Type"
					formInput={
						<SmartSelector
							selectBy={"type"}
							showBy={"type"}
							options={TYPE_OF_PHONES}
							selected={phoneType}
							disabledOptions={selectedTypePhones}
							listBoxProps={{}}
							popperProps={{ sx: { zIndex: `${1300}!important` } }}
							onChangeAction={(value) => handleChangePhone("type", (value as unknown as IPhone)?.type ?? "")}
							inputProps={{
								placeholder: "Phone Number Type",
							}}
							clearable
							disabled={disabled}
						/>
					}
				/>
			</Grid>
			{isDefaultPhoneField && (
				<Grid item xs={12} sm={4} className="flex items-end">
					<SimpleDoubleChecking
						className={"btn-delete !p-[5px]"}
						color="var(--gray-blue-color)"
						confirmationButtonColor="var(--gray-blue-color)"
						mainButtonClasses={"!top-[-2px] !left-[-2px]"}
						icon={<CloseRoundedIcon fontSize="small" />}
						iconHover={<CloseRoundedIcon fontSize="small" />}
						confirmationIcon={<SvgIcon className="!text-[25px] pl-[5px] pt-[2px]" component={TrashIcon} />}
						onDeleteAction={removePhoneNumber}
						size="small"
						disabled={disabled}
					/>
				</Grid>
			)}
		</Grid>
	);
}

export default CustomerContactSection;
